import { Box, Typography, Skeleton } from '@mui/material'
import { CSSProperties, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { assignApi } from 'hooks/api/assign'
import { HistoryItem } from 'types/assign'

import AssignCard from '../components/AssignCard'

const SKELETON_COUNT = 3

export default function AssignHistoryPage(): JSX.Element {
  const { t } = useTranslation('page-assign-history')
  const [searchParams] = useSearchParams()
  const contractDetailId = searchParams.get('contract_detail_id')
  const contractDetailName = searchParams.get('name') || ''
  const [historyData, setHistoryData] = useState<HistoryItem[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!contractDetailId) return

    const fetchHistory = async () => {
      try {
        setIsLoading(true)
        const response = await assignApi().getHistory(contractDetailId)
        setHistoryData(response.data.data)
      } catch (error) {
        console.error(error)
        setHistoryData([])
      } finally {
        setIsLoading(false)
      }
    }

    fetchHistory()
  }, [contractDetailId])

  return (
    <Box sx={containerStyle}>
      {contractDetailName && (
        <Typography sx={titleTextStyle}>
          {t('title', { name: contractDetailName })}
        </Typography>
      )}

      {isLoading ? (
        <Box sx={historyListStyle}>
          {Array.from({ length: SKELETON_COUNT }).map((_, index) => (
            <Box key={index} sx={historyItemCardStyle}>
              <Box sx={skeletonCardContentStyle}>
                <Box sx={dateTimeContainerStyle}>
                  <Skeleton variant="circular" width={24} height={24} />
                  <Skeleton variant="text" width={180} height={28} />
                </Box>

                <Box sx={contractDetailNameRowStyle}>
                  <Box sx={contractDetailNameContainerStyle}>
                    <Skeleton variant="circular" width={24} height={24} />
                    <Skeleton variant="text" width={150} height={28} />
                  </Box>
                  <Skeleton variant="circular" width={32} height={32} />
                </Box>

                <Box sx={addressContainerStyle}>
                  <Skeleton variant="circular" width={24} height={24} />
                  <Skeleton
                    variant="text"
                    sx={{ flex: 1, maxWidth: '80%' }}
                    height={24}
                  />
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      ) : historyData.length === 0 ? (
        <Box sx={emptyStateStyle}>
          <Typography sx={emptyTextStyle}>{t('empty')}</Typography>
        </Box>
      ) : (
        <Box sx={historyListStyle}>
          {historyData.map((item) => (
            <AssignCard
              key={item.assignId}
              assignId={item.assignId}
              date={item.date}
              startTime={item.startTime}
              endTime={item.endTime}
              contractDetailName={item.contractDetailName}
              address={item.address}
              securityNote={item.securityNote}
              securityReport={item.securityReport}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

// Styles
const containerStyle: CSSProperties = {
  width: '90%',
  margin: '32px auto 80px',
  overflowX: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
}

// タイトル
const titleTextStyle: CSSProperties = {
  color: '#333',
  fontSize: 20,
  fontWeight: 'bold',
  textAlign: 'center',
}

// 「履歴がありません」のエリア
const emptyStateStyle: CSSProperties = {
  backgroundColor: '#ffffff',
  padding: '20px',
  borderRadius: '12px',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  textAlign: 'center',
  marginTop: '40px',
}

const emptyTextStyle: CSSProperties = {
  fontSize: '16px',
  color: '#757575',
}

// 履歴リスト
const historyListStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  paddingBottom: '8px',
}

// スケルトン用スタイル
const historyItemCardStyle: CSSProperties = {
  backgroundColor: '#ffffff',
  border: '2px solid #f2f2f2',
  borderRadius: '8px',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  overflow: 'hidden',
}

const skeletonCardContentStyle: CSSProperties = {
  padding: '16px',
}

const dateTimeContainerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '12px',
}

const contractDetailNameRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '12px',
}

const contractDetailNameContainerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
}

const addressContainerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
}
